import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { US_STATES } from "@/lib/constants";
import { Upload, FileText, ArrowRight, CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const FIELD_LABELS = {
  first_name: "First Name",
  middle_name: "Middle Name",
  last_name: "Last Name",
  company: "Company",
  mailing_state: "Mailing State",
  county: "County",
  mailing_address: "Mailing Address",
  mailing_city: "Mailing City",
  mailing_zip: "Mailing Zip",
  license_no: "License No.",
  license_code: "License Code / Type",
  license_category: "Category",
  license_expires: "License Expires",
  email: "Email",
  phone: "Phone",
  notes: "Notes",
};

const STEPS = ["upload", "map", "import", "done"];

export default function ImportWizard({ open, onClose, onImported }) {
  const [step, setStep] = useState("upload");
  const [file, setFile] = useState(null);
  const [licensedState, setLicensedState] = useState("");
  const [source, setSource] = useState("");
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null); // {headers, sample, total, suggested_mapping, target_fields}
  const [mapping, setMapping] = useState({});
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const close = () => {
    setStep("upload");
    setFile(null);
    setLicensedState("");
    setSource("");
    setParsed(null);
    setMapping({});
    setResult(null);
    onClose();
  };

  const goParse = async () => {
    if (!file || !licensedState) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/imports/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setParsed(data);
      setMapping(data.suggested_mapping || {});
      setStep("map");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Parse failed");
    } finally {
      setParsing(false);
    }
  };

  const commit = async () => {
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Strip empty selections
      const cleaned = Object.fromEntries(
        Object.entries(mapping).filter(([, v]) => v && v !== "__none__")
      );
      fd.append("mapping", JSON.stringify(cleaned));
      fd.append("licensed_state", licensedState);
      fd.append("source", source);
      const { data } = await api.post("/imports/commit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      setStep("done");
      toast.success(`Imported ${data.inserted} contacts (${data.skipped} skipped)`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="import-wizard">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-3">
            <span>Import Roster</span>
            <StepPill current={step} />
          </DialogTitle>
          <DialogDescription>
            Drop a CSV or XLSX roster from any state board. We'll auto-detect columns and let you map any that don't.
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-4 py-2">
            <label
              htmlFor="iw-file"
              className="flex items-center gap-3 border border-dashed border-stone-300 rounded-sm p-5 cursor-pointer hover:border-[#B85C38] hover:bg-stone-50 transition-colors"
              data-testid="import-dropzone"
            >
              <FileText className="h-8 w-8 text-stone-400" />
              <div className="flex-1">
                <div className="text-sm font-medium">
                  {file ? file.name : "Choose a .csv or .xlsx roster"}
                </div>
                <div className="text-xs text-stone-500">
                  {file ? `${(file.size / 1024).toFixed(1)} KB` : "Click to browse"}
                </div>
              </div>
              <Input
                id="iw-file"
                type="file"
                accept=".csv,.xlsx,.xls,text/csv"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="import-file-input"
              />
            </label>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider">Licensed State *</Label>
                <Select value={licensedState} onValueChange={setLicensedState}>
                  <SelectTrigger className="mt-1.5 h-10 rounded-sm border-stone-300" data-testid="import-state-select">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {US_STATES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider">Source Name</Label>
                <Input
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  placeholder="e.g. Tennessee 2026-04 roster"
                  className="mt-1.5 h-10 rounded-sm border-stone-300"
                  data-testid="import-source-input"
                />
              </div>
            </div>
          </div>
        )}

        {step === "map" && parsed && (
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between border border-stone-300 bg-stone-50 px-4 py-2.5 rounded-sm">
              <div className="text-sm">
                <span className="font-mono text-xs text-stone-600">{parsed.filename}</span>
                <span className="mx-2 text-stone-400">·</span>
                <span className="font-medium">{parsed.total} rows</span>
                <span className="mx-2 text-stone-400">·</span>
                <span className="font-mono text-xs">{parsed.headers.length} columns</span>
              </div>
              <span className="font-mono text-[10px] uppercase tracking-wider text-stone-600">
                Licensed: {licensedState}
              </span>
            </div>

            <p className="text-sm text-stone-600">
              Auto-detected {Object.keys(parsed.suggested_mapping || {}).length} of {parsed.target_fields.length} fields.
              Adjust below if needed — set unused fields to <span className="font-mono text-xs bg-stone-100 px-1">Not in file</span>.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[40vh] overflow-y-auto pr-1">
              {parsed.target_fields.map((field) => (
                <div key={field} className="border border-stone-200 p-3 rounded-sm bg-white">
                  <Label className="text-[10px] font-mono uppercase tracking-[0.2em] text-stone-600">
                    {FIELD_LABELS[field] || field}
                  </Label>
                  <Select
                    value={mapping[field] || "__none__"}
                    onValueChange={(v) =>
                      setMapping((m) => ({ ...m, [field]: v === "__none__" ? "" : v }))
                    }
                  >
                    <SelectTrigger className="mt-1.5 h-9 rounded-sm border-stone-300" data-testid={`map-${field}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      <SelectItem value="__none__">— Not in file —</SelectItem>
                      {parsed.headers.map((h) => (
                        <SelectItem key={h} value={h}>{h}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {mapping[field] && parsed.sample[0]?.[mapping[field]] && (
                    <div className="mt-1.5 text-[11px] text-stone-500 font-mono truncate">
                      e.g. {String(parsed.sample[0][mapping[field]]).slice(0, 40)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {step === "done" && result && (
          <div className="py-4 space-y-4" data-testid="import-result">
            <div className="flex items-start gap-3 border border-[#4A5D23]/30 bg-[#4A5D23]/5 rounded-sm p-4">
              <CheckCircle2 className="h-5 w-5 text-[#4A5D23] flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-display text-lg font-bold tracking-tight">
                  Imported {result.inserted} contacts
                </div>
                <div className="text-sm text-stone-700 mt-1">
                  Tagged as <span className="font-mono">{result.source}</span> · licensed in <span className="font-mono">{result.licensed_state}</span>.
                  {result.skipped > 0 && (
                    <> Skipped <span className="font-mono">{result.skipped}</span> duplicate/invalid rows.</>
                  )}
                </div>
              </div>
            </div>
            {result.errors?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-stone-600">
                  {result.errors.length} skip warning(s)
                </summary>
                <ul className="mt-2 space-y-0.5 text-stone-600 max-h-40 overflow-y-auto">
                  {result.errors.map((er, i) => (
                    <li key={i} className="font-mono text-[11px]">{er}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {step === "map" && (
            <Button
              variant="outline"
              onClick={() => setStep("upload")}
              className="rounded-sm border-stone-300"
              data-testid="import-back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
            </Button>
          )}
          {step === "done" ? (
            <Button
              onClick={() => { onImported?.(); close(); }}
              className="rounded-sm bg-[#B85C38] hover:bg-[#9A4D2E] text-white"
              data-testid="import-finish-btn"
            >
              Done
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={close}
                className="rounded-sm border-stone-300"
                data-testid="import-cancel-btn"
              >
                Cancel
              </Button>
              {step === "upload" ? (
                <Button
                  onClick={goParse}
                  disabled={!file || !licensedState || parsing}
                  className="rounded-sm bg-[#B85C38] hover:bg-[#9A4D2E] text-white"
                  data-testid="import-next-btn"
                >
                  {parsing ? "Parsing…" : (
                    <span className="flex items-center">Next <ArrowRight className="h-4 w-4 ml-1.5" /></span>
                  )}
                </Button>
              ) : (
                <Button
                  onClick={commit}
                  disabled={committing}
                  className="rounded-sm bg-[#B85C38] hover:bg-[#9A4D2E] text-white"
                  data-testid="import-commit-btn"
                >
                  <Upload className="h-4 w-4 mr-1.5" />
                  {committing ? "Importing…" : `Import ${parsed?.total || ""} rows`}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StepPill({ current }) {
  const idx = STEPS.indexOf(current);
  return (
    <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-stone-500">
      step {Math.min(idx + 1, 3)} / 3
    </span>
  );
}
